import { type MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ScrollBox from '@erikdakoda/ui/components/ScrollBox';
import Spinner from '@erikdakoda/ui/components/Spinner';
import SearchBox from '@erikdakoda/ui/components/SearchBox';
import SidebarToolbar from '@erikdakoda/ui/components/SidebarToolbar';
import IconFab from '@erikdakoda/ui/components/IconFab';
import { useCreateTodoItem, useUpdateTodoItem } from '@erikdakoda/database/hooks/todo-item';
import { useCreateTodoList, useFindManyTodoList } from '@erikdakoda/database/hooks/todo-list';
import { useCurrentUser } from '@erikdakoda/ui/hooks/useCurrentUser';
import { useException } from '@erikdakoda/ui/hooks/useException';
import { useSnack } from '@erikdakoda/ui/hooks/useSnack';
import { log } from '@erikdakoda/log/log';
import { getNewTodoList } from '@erikdakoda/todo/shared/getNewTodoList';
import { getNewTodoItem } from '@erikdakoda/todo/shared/getNewTodoItem';
import { getTodoItemStateUpdateData } from '@erikdakoda/todo/shared/getTodoItemStateUpdateData';
import {
  TodoItemForSidebarSelect,
  TodoListForSidebarSelect,
  type TodoItemForSidebar,
} from '@erikdakoda/todo/types';
import { useTodoStore } from '../hooks/useTodoStore';
import type { LabelDragEndHandlerSetter } from '@erikdakoda/label-ui/types';
import type { TodoGroupListEntry } from '../shared/todoLabelSections';
import TodoSidebarList from './TodoSidebarList';

type GroupedSection = {
  group: string;
  lists: TodoGroupListEntry[];
};

type TodosSidebarProps = {
  setDragEndHandler?: LabelDragEndHandlerSetter;
};

/**
 * Left drawer sidebar listing todo items grouped by todo list group.
 */
export default function TodosSidebar({ setDragEndHandler }: TodosSidebarProps) {
  'use memo';

  const { t } = useTranslation('todo-ui');
  const { user } = useCurrentUser();
  const { snack } = useSnack();
  const exception = useException();
  const createTodoList = useCreateTodoList().mutateAsync;
  const createTodoItem = useCreateTodoItem().mutateAsync;
  const updateTodoItem = useUpdateTodoItem().mutateAsync;
  const todoFilter = useTodoStore((state) => state.todoFilter);
  const setTodoFilter = useTodoStore((state) => state.setTodoFilter);
  const selectedTodoId = useTodoStore((state) => state.selectedTodoId);
  const setSelectedTodoId = useTodoStore((state) => state.setSelectedTodoId);
  const clearSelectedTodo = useTodoStore((state) => state.clearSelectedTodo);
  const [loadingCreateTodo, setLoadingCreateTodo] = useState(false);
  const [loadingCreateList, setLoadingCreateList] = useState(false);

  const {
    data: todoLists,
    isLoading,
    queryKey,
    refetch,
  } = useFindManyTodoList(
    {
      where: {
        spaceId: user?.activeSpaceId,
        ownerId: user?.id,
      },
      orderBy: [{ group: 'asc' }, { ord: 'asc' }, { name: 'asc' }],
      select: TodoListForSidebarSelect,
    },
    { enabled: Boolean(user?.activeSpaceId && user?.id) },
  );

  const normalizedFilter = todoFilter.trim().toLowerCase();

  const byGroup = new Map<string, GroupedSection['lists']>();

  for (const list of todoLists ?? []) {
    const matchingItems = list.todoItems.filter((item) =>
      normalizedFilter ? item.name.toLowerCase().includes(normalizedFilter) : true,
    );
    const lists = byGroup.get(list.group) ?? [];

    if (list.group === 'user') {
      const items = matchingItems.filter((item) => item.state !== 'done');
      const doneItems = matchingItems.filter((item) => item.state === 'done');
      lists.push({ list, items, doneItems });
    } else {
      lists.push({ list, items: matchingItems });
    }

    byGroup.set(list.group, lists);
  }

  const groupedSections: GroupedSection[] = [...byGroup.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([group, lists]) => ({
      group,
      lists: lists.sort((a, b) => a.list.ord - b.list.ord),
    }));

  async function handleStateChange(id: string, state: TodoItemForSidebar['state']) {
    try {
      if (!user?.email) {
        return;
      }
      await updateTodoItem(
        {
          where: { id },
          data: getTodoItemStateUpdateData(state, { email: user.email, kind: 'human' }),
        },
        { optimisticUpdate: true },
      );
      //await refetch();
    } catch (err: unknown) {
      exception(err);
    }
  }

  function handleListTrashed(listId: string) {
    if (!selectedTodoId) return;
    const list = todoLists?.find((entry) => entry.id === listId);
    if (list?.todoItems.some((item) => item.id === selectedTodoId)) {
      clearSelectedTodo();
    }
  }

  function handleItemTrashed(todoId: string) {
    if (selectedTodoId === todoId) {
      clearSelectedTodo();
    }
  }

  function resolveTargetListId(): string | null {
    if (selectedTodoId && todoLists) {
      for (const list of todoLists) {
        if (list.todoItems.some((item) => item.id === selectedTodoId)) {
          return list.id;
        }
      }
    }
    const userList = todoLists?.find((list) => list.group === 'user');
    return userList?.id ?? todoLists?.[0]?.id ?? null;
  }

  async function handleCreateTodo(event: MouseEvent) {
    if (!user) {
      throw new Error('A user is required (handleCreateTodo)');
    }
    event.preventDefault();
    const todoListId = resolveTargetListId();
    if (!todoListId) {
      snack.error(t('createTodoListFirst'));
      return;
    }
    setLoadingCreateTodo(true);
    try {
      const targetList = todoLists?.find((list) => list.id === todoListId);
      const nextOrd =
        targetList?.todoItems.reduce((max, item) => Math.max(max, item.ord), -1) ?? -1;
      const newTodo = await createTodoItem({
        data: getNewTodoItem(user, todoListId, nextOrd + 1),
        select: TodoItemForSidebarSelect,
      });
      if (newTodo) {
        setSelectedTodoId(newTodo.id);
        await refetch();
      }
      snack.success(t('newTodoCreated'));
    } catch (err: unknown) {
      exception(err);
    } finally {
      setLoadingCreateTodo(false);
    }
  }

  async function handleCreateList(event: MouseEvent) {
    if (!user) {
      log('User is undefined (handleCreateList)', 'error');
      return;
    }
    event.preventDefault();
    setLoadingCreateList(true);
    try {
      const group = 'user';
      const groupLists = todoLists?.filter((list) => list.group === group) ?? [];
      const nextOrd = groupLists.reduce((max, list) => Math.max(max, list.ord), -1);
      const newList = await createTodoList({
        data: getNewTodoList(user, { name: 'New List', group, ord: nextOrd + 1 }),
        select: { id: true },
      });
      if (newList) {
        snack.success(t('newTodoListCreated'));
        await refetch();
      }
    } catch (err: unknown) {
      exception(err);
    } finally {
      setLoadingCreateList(false);
    }
  }

  const hasLists = (todoLists?.length ?? 0) > 0;
  const filterActive = normalizedFilter.length > 0;

  return (
    <Box
      className="todos-sidebar"
      sx={{ height: '100%', display: 'grid', gridTemplateRows: 'min-content 1fr' }}
    >
      <SidebarToolbar
        title="Todos"
        icon="clipboard-check"
        refetch={async () => {
          await refetch();
        }}
        searchBox={<SearchBox setFilter={setTodoFilter} defaultFilter={todoFilter} />}
        searchValue={todoFilter}
      />
      <ScrollBox disableTopFade overflowXHidden>
        {isLoading ? (
          <Spinner sx={{ mt: 4 }} />
        ) : !hasLists ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              px: 2,
              py: 2,
            }}
          >
            {t('noTodoListsYet')}
          </Typography>
        ) : (
          <TodoSidebarList
            groupedSections={groupedSections}
            filterActive={filterActive}
            selectedTodoId={selectedTodoId}
            onSelect={setSelectedTodoId}
            onStateChange={handleStateChange}
            onTrashed={handleListTrashed}
            onItemTrashed={handleItemTrashed}
            refetch={async () => {
              await refetch();
            }}
            queryKey={queryKey}
            setDragEndHandler={setDragEndHandler}
            todoLists={todoLists ?? []}
            ownerId={user?.id ?? ''}
            spaceId={user?.activeSpaceId ?? null}
          />
        )}
      </ScrollBox>
      <Box
        sx={{
          position: 'absolute',
          pointerEvents: 'none',
          bottom: 16,
          right: 0,
          left: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 4,
        }}
      >
        <IconFab
          icon="light-clipboard-plus"
          title={t('newTodo')}
          onClick={handleCreateTodo}
          tooltipPlacement="top"
          loading={loadingCreateTodo}
          sx={{ pointerEvents: 'auto' }}
        />
        <IconFab
          icon="folder-plus"
          title={t('newList')}
          onClick={handleCreateList}
          tooltipPlacement="top"
          loading={loadingCreateList}
          sx={{ pointerEvents: 'auto' }}
        />
      </Box>
    </Box>
  );
}
